import { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { CreatePersonPayload, Person } from 'services/person/person.types';
import { useCreatePersonMutation, useGetPersonManagersMutation } from 'services/person/person.endpoint';
import { useNavigate } from 'react-router-dom';
import { useApiToast } from 'components/Toaster';
import { User } from 'types/User';
import { useGetAllUsersMutation } from 'services/user/user.endpoint';

export const useNewPersonVM = () => {
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState<number>(1);
    const [isQuickAdd, setIsQuickAdd] = useState<boolean>(true);
    const [isSuccess, setIsSuccess] = useState<boolean>(false);
    const [users, setUsers] = useState<Array<User>>([]);
    const steps = ['Profil', 'GED', 'Caractéristiques'];
    const [person, setPerson] = useState<Person>({} as Person);
    const { showErrorToast } = useApiToast();
    const [personManagersOptions, setPersonManagersOptions] = useState<{ label: string; value: number | null }[]>([]);

    const [
        createPerson,
        {
            isLoading: isCreatePersonLoading,
            isError: isCreatePersonError,
            isSuccess: isCreatePersonSuccess,
            error: createPersonError,
            data: createPersonData
        }
    ] = useCreatePersonMutation();

    useEffect(() => {
        getAllUsers(undefined);
    }, []);

    useEffect(() => {
        if (isCreatePersonLoading) return;
        if (isCreatePersonError && createPersonError) {
            showErrorToast(createPersonError, {
                message: 'Erreur lors de la création de la personne'
            });
        }
        if (isCreatePersonSuccess && createPersonData) {
            return;
        }
    }, [isCreatePersonLoading, isCreatePersonError, isCreatePersonSuccess, createPersonError, createPersonData, showErrorToast]);

    const handleQuickCreatePerson = async (data: CreatePersonPayload) => {
        const response = await createPerson(data);
        setIsSuccess(true);
        setTimeout(() => {
            if (response?.data?.data?.id) {
                navigate(`/app/admin/persons/${response.data.data.id}`);
                setIsSuccess(false);
                setIsQuickAdd(true);
                setActiveStep(1);
            }
        }, 3000);
    };

    const [
        getAllUsers,
        {
            isLoading: isGetAllUsersLoading,
            isError: isGetAllUsersError,
            isSuccess: isGetAllUsersSuccess,
            error: getAllUsersError,
            data: getAllUsersData
        }
    ] = useGetAllUsersMutation();

    const handlePreviousStep = useCallback(
        (data: CreatePersonPayload) => {
            setActiveStep((prevState) => {
                return prevState - 1;
            });
        },
        [activeStep]
    );

    const handleNextStep = (data: CreatePersonPayload) => {
        setPerson((prevState) => ({
            ...prevState,
            ...data
        }));
        setActiveStep((prevState) => prevState + 1);
    };

    const handleCreatePerson = async (data: CreatePersonPayload) => {
        if (!person) return;

        const response = await createPerson({
            ...person,
            ...data
        });
        setIsSuccess(true);
        setTimeout(() => {
            if (response?.data?.data?.id) {
                navigate(`/app/admin/persons/${response.data.data.id}`);
                setIsSuccess(false);
                setIsQuickAdd(true);
                setActiveStep(1);
            }
        }, 3000);
    };

    useEffect(() => {
        if (isGetAllUsersLoading) return;
        if (isGetAllUsersError && getAllUsersError) {
            showErrorToast(getAllUsersError, {
                message: 'Erreur lors de la récupération des utilisateurs'
            });
            return;
        }
        if (isGetAllUsersSuccess && getAllUsersData) {
            setUsers(getAllUsersData.data);
            return;
        }
    }, [isGetAllUsersLoading, isGetAllUsersError, isGetAllUsersSuccess, getAllUsersError, getAllUsersData]);

    return {
        activeStep,
        steps,
        setActiveStep,
        handleNextStep,
        isQuickAdd,
        setIsQuickAdd,
        handleCreatePerson,
        handleQuickCreatePerson,
        isSuccess,
        setIsSuccess,
        handlePreviousStep,
        person,
        users,
    };
};
